/**
 * Geometry of the block lightbox: the authored box, zoomed visually.
 *
 * Transform-zoom semantics — the block renders at its AUTHORED width and is
 * scaled, never reflowed, so the layout its author designed is what shows
 * large. Growth caps at 2× the authored box: fill-the-viewport zoom made
 * small blocks absurd (a one-word 160-wide badge blown up 8×), so the
 * ceiling tracks the block's own size the way every other default does — a
 * default-sized 320-wide block tops out at 640, big dashboards are bounded
 * by the viewport fit instead.
 *
 * The node's box is a window, not the document: the frame takes the FULL
 * content height (reported by the runtime across the opaque origin) so
 * nothing is clipped inside it, and the overlay is the scroll container.
 */
export const LIGHTBOX_VIEWPORT_FIT = { width: 0.92, height: 0.88 } as const;
export const LIGHTBOX_MAX_SCALE = 2;

export interface LightboxBoxInput {
  viewportWidth: number;
  viewportHeight: number;
  /** The node's authored footprint. */
  baseWidth: number;
  baseHeight: number;
  /** Natural document height at the authored width, when known. */
  contentHeight: number | null;
}

export interface LightboxBox {
  /** Visual zoom applied to the authored box. */
  scale: number;
  /** Authored-size frame height: the content's full extent, never less than
   *  the node's own height. */
  frameHeight: number;
  /** On-screen footprint the card reserves (transforms don't affect layout). */
  width: number;
  height: number;
}

export function computeLightboxBox({
  viewportWidth,
  viewportHeight,
  baseWidth,
  baseHeight,
  contentHeight,
}: LightboxBoxInput): LightboxBox {
  const frameHeight = Math.max(baseHeight, contentHeight ?? 0);
  const fitScale =
    baseWidth > 0 && baseHeight > 0
      ? Math.min(
          (viewportWidth * LIGHTBOX_VIEWPORT_FIT.width) / baseWidth,
          (viewportHeight * LIGHTBOX_VIEWPORT_FIT.height) / baseHeight,
        )
      : 1;
  const scale = Math.min(
    Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1,
    LIGHTBOX_MAX_SCALE,
  );
  return {
    scale,
    frameHeight,
    width: Math.round(baseWidth * scale),
    height: Math.round(frameHeight * scale),
  };
}
